import { useEffect, useId, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { salesTargetsAPI, incentiveAPI } from '../services/api';
import { downloadXlsx } from '../utils/download';
import type { ReferenceField, Scope } from '../pages/salesTargets/types';

/**
 * "Download sheet" for Master Data → Sales & Targets, with the choice of which
 * store / employee details to add beside each row.
 *
 * Every project keeps different details — iPro steers targets by a store's
 * Staffing Type and checks sales against a promoter's DS Person ID Type — so the
 * choice is the project's own custom field list, fetched when the button is first
 * clicked. The columns are read-only: the upload recognises and ignores them.
 *
 * The monthly sheets also ask WHICH PRODUCTS to carry. One sheet has always held
 * every product, and the upload reads every product column it is given — but the
 * buttons sit beside the page's Product box, which made that look like one file
 * per product. So the choice is put in words: all products, or only the one
 * chosen on the page. A project with one product and no custom fields has
 * nothing to choose and downloads straight away, exactly as before.
 *
 * One component for the grid toolbar and the upload modal, so the two download
 * buttons cannot drift apart. A popover rather than a modal, because one of them
 * already lives inside a modal.
 */

export type SalesSheetKind = 'target' | 'sales' | 'sales-detailed';

interface Props {
  projectId: number;
  month: string;
  sheet: SalesSheetKind;
  /** Whose rows the sheet lists. The per-sale sheet is always a promoter's. */
  scope: Scope;
  label: string;
  /**
   * The product chosen on the page, offered as "only this product". Monthly
   * sheets only: the per-sale sheet names its product on each row.
   */
  product?: { key: string; label: string } | null;
  /** How many products the full sheet carries. The choice is offered from two. */
  productCount?: number;
  /** Below a toolbar button, or above a modal footer button. */
  placement?: 'below' | 'above';
  className?: string;
  /** Null clears a previous error when a new attempt starts. */
  onError: (message: string | null) => void;
}

const DEFAULT_BUTTON = 'flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60';

// The last choice per project and sheet, so next month's download is one click.
// A convenience only: private mode or a full quota just means nothing is remembered.
const readChoice = (key: string): string[] => {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

const saveChoice = (key: string, ids: string[]): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    /* not remembered — see readChoice */
  }
};

/** A blob download's error arrives as a Blob, so its JSON message has to be read out. */
const downloadErrorMessage = async (err: any, fallback: string): Promise<string> => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.message) return String(parsed.message);
    } catch {
      /* not JSON */
    }
  }
  return data?.message || fallback;
};

/** A product's name as part of a file name. */
const fileSlug = (label: string): string =>
  label.trim().replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'product';

export default function SalesSheetDownloadButton({
  projectId, month, sheet, scope, label, product = null, productCount = 0,
  placement = 'below', className, onError,
}: Props) {
  const rowScope: Scope = sheet === 'sales-detailed' ? 'employee' : scope;
  const choiceKey = `salesTargets.referenceFields.${projectId}.${sheet}.${rowScope}`;
  const canChooseProduct = sheet !== 'sales-detailed' && product !== null && productCount > 1;
  const radioName = useId();

  /** Null until first asked for; refetched when the project or row scope changes. */
  const [fields, setFields] = useState<ReferenceField[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set<string>());
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Never remembered: a narrowed sheet is asked for each time, so "all products"
  // is what a click-through gets.
  const [onlyProduct, setOnlyProduct] = useState(false);

  useEffect(() => {
    setFields(null);
    setOpen(false);
  }, [projectId, rowScope]);

  const download = async (ids: string[], single = false) => {
    setBusy(true);
    onError(null);
    try {
      if (sheet === 'sales-detailed') {
        const res = await incentiveAPI.downloadDetailedTemplate(projectId, month, ids);
        downloadXlsx(`detailed_sales_${month}.xlsx`, res.data);
      } else {
        const only = single && canChooseProduct && product ? product : null;
        const products = only ? [only.key] : [];
        const res = sheet === 'sales'
          ? await salesTargetsAPI.downloadSalesTemplate(projectId, month, scope, ids, products)
          : await salesTargetsAPI.downloadTargetTemplate(projectId, month, scope, ids, products);
        // Named for its product, so a narrowed sheet is not mistaken for the full one.
        const suffix = only ? `_${fileSlug(only.label)}` : '';
        downloadXlsx(`${sheet === 'sales' ? 'sales' : 'sales_targets'}_${scope}_${month}${suffix}.xlsx`, res.data);
      }
      setOpen(false);
    } catch (err: any) {
      onError(await downloadErrorMessage(err, 'Could not build the sheet.'));
    } finally {
      setBusy(false);
    }
  };

  const handleClick = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    let list = fields;
    if (list === null) {
      setBusy(true);
      try {
        const res = await salesTargetsAPI.listReferenceFields(projectId, rowScope);
        list = (res.data.data?.fields ?? []) as ReferenceField[];
        setFields(list);
      } catch {
        // The extra columns are optional; not being able to list them must not
        // stop the sheet itself. Not cached, so the next click asks again.
        list = [];
      } finally {
        setBusy(false);
      }
    }
    if (list.length === 0 && !canChooseProduct) {
      await download([]);
      return;
    }
    const remembered = new Set(readChoice(choiceKey));
    setSelected(new Set(list.filter((f) => remembered.has(f.id)).map((f) => f.id)));
    setOnlyProduct(false);
    setOpen(true);
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirm = () => {
    // In the order the server lists them, which is the order the columns appear.
    const ids = (fields ?? []).filter((f) => selected.has(f.id)).map((f) => f.id);
    // Only when there was a list to choose from: a failed fetch must not wipe
    // what was remembered.
    if (fields !== null && fields.length > 0) saveChoice(choiceKey, ids);
    void download(ids, onlyProduct);
  };

  const listed = fields ?? [];
  const groups: { entity: string; title: string; hint: string | null; items: ReferenceField[] }[] = [
    {
      entity: 'employee',
      title: 'Employee fields',
      hint: null,
      items: listed.filter((f) => f.entity === 'employee'),
    },
    {
      entity: 'store',
      title: 'Store fields',
      hint: rowScope === 'employee' ? "Of the store in each row's Store Code column." : null,
      items: listed.filter((f) => f.entity === 'store'),
    },
  ].filter((g) => g.items.length > 0);

  const allSelected = fields !== null && fields.length > 0 && selected.size === fields.length;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => { void handleClick(); }}
        disabled={busy}
        aria-expanded={open}
        className={className ?? DEFAULT_BUTTON}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {label}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={`absolute z-50 w-80 rounded-lg border border-gray-200 bg-white p-4 text-left shadow-xl ${
              // Opening upward inside a modal there is nowhere to scroll to, so the
              // height is capped and the reference list is the part that gives way.
              placement === 'above'
                ? 'bottom-full left-0 mb-2 flex max-h-[calc(50vh+7rem)] flex-col'
                : 'right-0 top-full mt-2'
            }`}
          >
            {canChooseProduct && product && (
              <fieldset className="min-w-0">
                <legend className="text-sm font-semibold text-gray-900">Products in the sheet</legend>
                <p className="mt-1 text-xs text-gray-500">
                  One file can carry every product. The upload reads the product columns a file
                  has and leaves every other product as it is.
                </p>
                <div className="mt-2 space-y-0.5">
                  <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm text-gray-700 hover:bg-gray-50">
                    <input
                      type="radio"
                      name={radioName}
                      checked={!onlyProduct}
                      onChange={() => setOnlyProduct(false)}
                      className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>All {productCount} products, in one file</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm text-gray-700 hover:bg-gray-50">
                    <input
                      type="radio"
                      name={radioName}
                      checked={onlyProduct}
                      onChange={() => setOnlyProduct(true)}
                      className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    {/* Wrapped, not cut short: versions of one product differ at the end. */}
                    <span className="min-w-0 break-words">Only {product.label}</span>
                  </label>
                </div>
              </fieldset>
            )}

            {listed.length > 0 && (
              <div className={`flex min-h-0 flex-col ${canChooseProduct ? 'mt-4 border-t border-gray-100 pt-3' : ''}`}>
                <p className="text-sm font-semibold text-gray-900">Add reference columns</p>
                <p className="mt-1 text-xs text-gray-500">
                  Shown beside each row to help fill the sheet in. They are not saved when the
                  sheet is uploaded — change them in Master Data.
                </p>

                <div className="mt-3 max-h-64 min-h-0 space-y-3 overflow-y-auto">
                  {groups.map((g) => (
                    <fieldset key={g.entity}>
                      <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {g.title}
                      </legend>
                      {g.hint && <p className="text-xs text-gray-400">{g.hint}</p>}
                      <div className="mt-1 space-y-0.5">
                        {g.items.map((f) => (
                          <label
                            key={f.id}
                            className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={selected.has(f.id)}
                              onChange={() => toggle(f.id)}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="truncate">{f.label}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ))}
                </div>
              </div>
            )}

            <div
              className={`mt-4 flex items-center gap-2 border-t border-gray-100 pt-3 ${
                listed.length > 0 ? 'justify-between' : 'justify-end'
              }`}
            >
              {listed.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected(allSelected ? new Set<string>() : new Set(listed.map((f) => f.id)))}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700"
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
              )}
              <button
                type="button"
                onClick={confirm}
                disabled={busy}
                className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {selected.size > 0 ? `Download with ${selected.size} extra` : 'Download'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
